// =============================================================
// REPLAY 2D — trajectoires post-game sur la minimap (Chantier 2)
// Données : GET /api/paths (timeline Riot reconstruite, 1 point/min/joueur).
// Canvas responsive : fond de carte DataDragon, positions interpolées au
// temps t, trails par joueur, marqueurs kills/objectifs/tours/recalls,
// curseur temporel play/pause + vitesse, légende sélection/masquage.
// =============================================================
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Spinner, ErrorState, EmptyState } from './components';
import { getChamp, allChamps, champSquare, imgPath } from './ddragon';
import { useApi } from './useApi';
import { fetchPaths } from './api';

// Domaine de coordonnées de la Faille (repère Riot, y vers le haut).
const MAP = { minX: -120, minY: -120, maxX: 14870, maxY: 14980 };
// Vitesses de lecture : secondes de jeu par seconde réelle.
const SPEEDS = [2, 5, 10, 20];
const TRAIL_MS = 90_000;   // longueur du sillage derrière chaque joueur
const KILL_MS = 10_000;    // durée d'affichage d'un marqueur de kill
const OBJ_MS = 15_000;     // durée d'affichage d'un objectif
const RECALL_MS = 8_000;   // durée d'affichage d'un rappel

// Nuances par équipe (5 joueurs distinguables, bleu vs rouge).
const TEAM_SHADES = {
  100: ['#60A5FA', '#38BDF8', '#818CF8', '#22D3EE', '#93C5FD'],
  200: ['#F87171', '#FB923C', '#F472B6', '#FBBF24', '#FCA5A5'],
};
const TEAM_COLOR = { 100: '#3B82F6', 200: '#EF4444' };

const DRAGON_LABELS = {
  FIRE_DRAGON: 'Drake infernal', WATER_DRAGON: 'Drake océanique',
  EARTH_DRAGON: 'Drake tellurique', AIR_DRAGON: 'Drake des nuées',
  HEXTECH_DRAGON: 'Drake hextech', CHEMTECH_DRAGON: 'Drake chimtech',
  ELDER_DRAGON: 'Drake ancestral',
};
const MONSTER_META = {
  DRAGON:       { letter: 'D', label: (e) => DRAGON_LABELS[e.monsterSubType] || 'Drake' },
  RIFTHERALD:   { letter: 'H', label: () => 'Héraut de la Faille' },
  BARON_NASHOR: { letter: 'B', label: () => 'Baron Nashor' },
  HORDE:        { letter: 'L', label: () => 'Larve du Néant' },
  ATAKHAN:      { letter: 'A', label: () => 'Atakhan' },
};
const TOWER_LABELS = {
  OUTER_TURRET: 'Tour externe', INNER_TURRET: 'Tour intérieure',
  BASE_TURRET: 'Tour de base', NEXUS_TURRET: 'Tour du Nexus',
};
const LANE_LABELS = { TOP_LANE: 'voie du haut', MID_LANE: 'voie du milieu', BOT_LANE: 'voie du bas' };

const fmtClock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Position interpolée linéairement au temps t sur un chemin [{t,x,y}] trié. */
function posAt(path, t) {
  if (!path || !path.length) return null;
  if (t <= path[0].t) return path[0];
  const last = path[path.length - 1];
  if (t >= last.t) return last;
  let lo = 0, hi = path.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (path[mid].t <= t) lo = mid; else hi = mid;
  }
  const a = path[lo], b = path[hi];
  const r = (t - a.t) / ((b.t - a.t) || 1);
  return { x: a.x + r * (b.x - a.x), y: a.y + r * (b.y - a.y) };
}

/** Points du chemin dans [from, to], bornes interpolées incluses. */
function trailPoints(path, from, to) {
  const start = posAt(path, from);
  if (!start) return [];
  const pts = [{ t: Math.max(from, path[0].t), x: start.x, y: start.y }];
  for (const pt of path) if (pt.t > from && pt.t < to) pts.push(pt);
  const end = posAt(path, to);
  if (end) pts.push({ t: Math.min(to, path[path.length - 1].t), x: end.x, y: end.y });
  return pts;
}

// Files jouées sur la Faille (fallback tant que l'API n'expose pas mapId).
const SR_QUEUE_IDS = new Set([400, 420, 430, 440, 490, 700, 830, 840, 850, 870, 880, 890, 900, 1400, 1900]);
/** Vrai si la partie s'est jouée sur la Faille de l'invocateur (map 11). */
export function isSummonersRift(match) {
  if (!match) return false;
  if (match.mapId !== null && match.mapId !== undefined) return Number(match.mapId) === 11;
  return SR_QUEUE_IDS.has(Number(match.queueId));
}

// ---------- Petites icônes locales (pas dans components.jsx) ----------
const svgProps = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const PlayIcon = () => <svg {...svgProps}><path d="M6 4v16l14-8L6 4z" fill="currentColor" stroke="none"/></svg>;
const PauseIcon = () => <svg {...svgProps}><path d="M7 4v16M17 4v16" strokeWidth="3"/></svg>;
const EyeIcon = ({ off }) => (
  <svg {...svgProps} width={13} height={13}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
    {off && <path d="M3 3l18 18"/>}
  </svg>
);

// ============================================================
// Composant public : gère chargement / erreur / indisponible.
// ============================================================
export default function MatchReplay({ matchId }) {
  const state = useApi(
    matchId ? `paths-${matchId}` : null,
    () => fetchPaths(matchId, { densify: 0 }),
    { ttl: 30 * 60_000 }, // la timeline d'un match terminé ne bouge plus
  );

  if (state.loading && !state.data) return <Spinner label="Reconstruction des trajectoires…"/>;
  if (state.error) {
    // paths.js répond 500 "échec récupération/traitement" quand la timeline
    // Riot n'existe plus (match trop ancien) : indisponible plutôt qu'erreur.
    const msg = String(state.error.message || state.error);
    if (/échec récupération|introuvable|404/i.test(msg)) {
      return <EmptyState icon="clock" title="Replay indisponible"
                         hint="La timeline Riot n'existe plus pour cette partie (match trop ancien)."/>;
    }
    return <ErrorState error={state.error} onRetry={state.refetch}/>;
  }
  const data = state.data;
  if (!data?.participants?.some((p) => (p.path || []).length > 1)) {
    return <EmptyState icon="clock" title="Replay indisponible"
                       hint="Aucune trajectoire exploitable dans la timeline de cette partie."/>;
  }
  return <ReplayCanvas data={data}/>;
}

// ============================================================
// Le replay proprement dit (canvas + contrôles + légende).
// ============================================================
function ReplayCanvas({ data }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const hitsRef = useRef([]); // cibles survolables du dernier rendu [{x,y,r,title,sub}]

  const [size, setSize] = useState(0);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);
  const [hidden, setHidden] = useState({});      // participantId -> true
  const [selected, setSelected] = useState(null); // participantId | null
  const [tooltip, setTooltip] = useState(null);   // { x, y, title, sub }
  const [, setAssetTick] = useState(0);           // force un redraw quand carte/icône arrive

  // Joueurs enrichis : couleur par équipe, id/nom ddragon du champion
  // (match-v5 renvoie "FiddleSticks" là où ddragon attend "Fiddlesticks").
  const players = useMemo(() => {
    const teamIdx = { 100: 0, 200: 0 };
    return (data.participants || []).map((p) => {
      const shades = TEAM_SHADES[p.teamId] || TEAM_SHADES[100];
      const color = shades[teamIdx[p.teamId]++ % shades.length];
      const dd = getChamp(p.championName)
        || allChamps().find((c) => c.id.toLowerCase() === String(p.championName).toLowerCase())
        || null;
      return { ...p, color, champId: dd?.id || p.championName, champLabel: dd?.name || p.championName };
    });
  }, [data]);
  const byPid = useMemo(() => Object.fromEntries(players.map((p) => [p.participantId, p])), [players]);
  const nameOf = useCallback((pid) => byPid[pid]?.champLabel || (pid ? `J${pid}` : '?'), [byPid]);

  const duration = useMemo(() => {
    let max = 0;
    for (const p of players) { const last = p.path?.[p.path.length - 1]; if (last?.t > max) max = last.t; }
    for (const list of Object.values(data.events || {})) {
      for (const e of list) if (e.timestamp > max) max = e.timestamp;
    }
    return max || 1;
  }, [players, data]);

  // ---- Fond de carte (DataDragon map11) ----
  const mapImgRef = useRef(null);
  useEffect(() => {
    const img = new Image();
    img.onload = () => { mapImgRef.current = img; setAssetTick((n) => n + 1); };
    img.src = imgPath('map', 'map11.png');
  }, []);

  // ---- Icônes de champions (cache, chargées à la demande) ----
  const iconsRef = useRef({});
  const iconFor = useCallback((champ) => {
    let entry = iconsRef.current[champ];
    if (!entry) {
      const img = new Image();
      entry = { img, ready: false };
      img.onload = () => { entry.ready = true; setAssetTick((n) => n + 1); };
      img.src = champSquare(champ);
      iconsRef.current[champ] = entry;
    }
    return entry.ready ? entry.img : null;
  }, []);

  // ---- Taille responsive ----
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) setSize(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Horloge de lecture ----
  useEffect(() => {
    if (!playing) return undefined;
    let raf, last = performance.now();
    const step = (now) => {
      const dt = now - last; last = now;
      setT((prev) => {
        const next = prev + dt * speed;
        if (next >= duration) { setPlaying(false); return duration; }
        return next;
      });
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, duration]);

  // ---- Projection jeu -> canvas (y inversé) ----
  const toPx = useCallback((pt) => [
    ((pt.x - MAP.minX) / (MAP.maxX - MAP.minX)) * size,
    (1 - (pt.y - MAP.minY) / (MAP.maxY - MAP.minY)) * size,
  ], [size]);

  // ---- Rendu canvas ----
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size) return;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.round(size * dpr);
    if (canvas.width !== px) { canvas.width = px; canvas.height = px; }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const hits = [];

    // 1) Fond de carte
    if (mapImgRef.current) {
      ctx.drawImage(mapImgRef.current, 0, 0, size, size);
      ctx.fillStyle = 'rgba(8, 10, 14, 0.22)'; // voile pour le contraste des tracés
      ctx.fillRect(0, 0, size, size);
    } else {
      ctx.fillStyle = '#10141B';
      ctx.fillRect(0, 0, size, size);
    }

    const dimOthers = selected !== null;

    // 2) Bâtiments détruits (persistants)
    for (const b of data.events?.buildings || []) {
      if (b.timestamp > t || !b.position) continue;
      const [x, y] = toPx(b.position);
      const c = TEAM_COLOR[b.teamId] || '#9CA3AF'; // teamId = équipe qui perd le bâtiment
      ctx.save();
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = c;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - 4, y - 4); ctx.lineTo(x + 4, y + 4);
      ctx.moveTo(x + 4, y - 4); ctx.lineTo(x - 4, y + 4);
      ctx.stroke();
      ctx.restore();
      const isInhib = b.buildingType === 'INHIBITOR_BUILDING';
      const tower = isInhib ? 'Inhibiteur' : (TOWER_LABELS[b.towerType] || 'Bâtiment');
      const lane = LANE_LABELS[b.laneType];
      hits.push({
        x, y, r: 8,
        title: `${tower}${lane ? ` (${lane})` : ''} ${isInhib ? 'détruit' : 'détruite'}`,
        sub: `${fmtClock(b.timestamp)} · par ${nameOf(b.killerId)}`,
      });
    }

    // 3) Sillages
    for (const p of players) {
      if (hidden[p.participantId]) continue;
      const isSel = selected === p.participantId;
      const baseAlpha = dimOthers && !isSel ? 0.16 : 0.85;

      // Joueur sélectionné : trajet complet depuis le début
      if (isSel) {
        const full = trailPoints(p.path, 0, t);
        if (full.length > 1) {
          ctx.save();
          ctx.globalAlpha = 0.32;
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          full.forEach((pt, i) => { const [x, y] = toPx(pt); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
          ctx.stroke();
          ctx.restore();
        }
      }

      const pts = trailPoints(p.path, Math.max(0, t - TRAIL_MS), t);
      ctx.save();
      ctx.strokeStyle = p.color;
      ctx.lineCap = 'round';
      for (let i = 0; i < pts.length - 1; i++) {
        const age = t > 0 ? (pts[i + 1].t - (t - TRAIL_MS)) / TRAIL_MS : 1;
        ctx.globalAlpha = Math.max(0.05, Math.min(1, age)) * baseAlpha;
        ctx.lineWidth = isSel ? 3 : 2.2;
        const [x1, y1] = toPx(pts[i]);
        const [x2, y2] = toPx(pts[i + 1]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }
      ctx.restore();
    }

    // 4) Rappels (anneaux à la position interpolée du joueur)
    for (const r of data.events?.recalls || []) {
      const age = t - r.timestamp;
      if (age < 0 || age > RECALL_MS) continue;
      const p = byPid[r.participantId];
      if (!p || hidden[p.participantId]) continue;
      const pos = posAt(p.path, r.timestamp);
      if (!pos) continue;
      const [x, y] = toPx(pos);
      const k = age / RECALL_MS;
      ctx.save();
      ctx.globalAlpha = (1 - k) * 0.9;
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(x, y, 6 + k * 10, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 3 + k * 5, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      hits.push({ x, y, r: 10, title: `Rappel — ${p.champLabel}`, sub: fmtClock(r.timestamp) });
    }

    // 5) Kills (impulsion à la position du kill)
    for (const kEv of data.events?.kills || []) {
      const age = t - kEv.timestamp;
      if (age < 0 || age > KILL_MS || !kEv.position) continue;
      const [x, y] = toPx(kEv.position);
      const killerTeam = kEv.killerId >= 1 ? byPid[kEv.killerId]?.teamId : null;
      const c = TEAM_COLOR[killerTeam] || '#9CA3AF';
      const k = age / KILL_MS;
      ctx.save();
      ctx.globalAlpha = 1 - k * 0.6;
      ctx.fillStyle = c;
      ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x - 2.5, y - 2.5); ctx.lineTo(x + 2.5, y + 2.5);
      ctx.moveTo(x + 2.5, y - 2.5); ctx.lineTo(x - 2.5, y + 2.5);
      ctx.stroke();
      ctx.globalAlpha = (1 - k) * 0.5;
      ctx.strokeStyle = c;
      ctx.beginPath(); ctx.arc(x, y, 6 + k * 14, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      const assists = (kEv.assistingParticipantIds || []).length;
      hits.push({
        x, y, r: 10,
        title: kEv.killerId >= 1
          ? `${nameOf(kEv.killerId)} élimine ${nameOf(kEv.victimId)}`
          : `Exécution de ${nameOf(kEv.victimId)}`,
        sub: `${fmtClock(kEv.timestamp)}${assists ? ` · ${assists} assist${assists > 1 ? 's' : ''}` : ''}`,
      });
    }

    // 6) Objectifs (losange lettré)
    for (const o of data.events?.objectives || []) {
      const age = t - o.timestamp;
      if (age < 0 || age > OBJ_MS || !o.position) continue;
      const meta = MONSTER_META[o.monsterType] || { letter: 'M', label: () => o.monsterType || 'Monstre' };
      const [x, y] = toPx(o.position);
      const c = TEAM_COLOR[o.killerTeamId] || '#A78BFA';
      const k = age / OBJ_MS;
      const s = 8;
      ctx.save();
      ctx.globalAlpha = 1 - k * 0.5;
      ctx.fillStyle = c;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x, y - s); ctx.lineTo(x + s, y); ctx.lineTo(x, y + s); ctx.lineTo(x - s, y);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = '700 9px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(meta.letter, x, y + 0.5);
      ctx.restore();
      hits.push({
        x, y, r: 11,
        title: meta.label(o),
        sub: `${fmtClock(o.timestamp)} · pris par ${nameOf(o.killerId)} (équipe ${o.killerTeamId === 100 ? 'bleue' : 'rouge'})`,
      });
    }

    // 7) Positions courantes (icône champion clipée en cercle)
    for (const p of players) {
      if (hidden[p.participantId]) continue;
      const pos = posAt(p.path, t);
      if (!pos) continue;
      const isSel = selected === p.participantId;
      const [x, y] = toPx(pos);
      const r = isSel ? 12 : 9;
      ctx.save();
      ctx.globalAlpha = dimOthers && !isSel ? 0.35 : 1;
      const icon = iconFor(p.champId);
      if (icon) {
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.closePath();
        ctx.save(); ctx.clip();
        ctx.drawImage(icon, x - r, y - r, r * 2, r * 2);
        ctx.restore();
      } else {
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.lineWidth = isSel ? 2.5 : 2;
      ctx.strokeStyle = p.color;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      hits.push({
        x, y, r: r + 3,
        title: `${p.champLabel} — ${p.summonerName}`,
        sub: `Position à ${fmtClock(t)}`,
      });
    }

    hitsRef.current = hits;
  }, [size, t, players, byPid, hidden, selected, data, toPx, iconFor, nameOf]);

  // Redessine après chaque rendu (t, taille, filtres, icônes/carte arrivées via assetTick).
  useEffect(() => { draw(); });

  // ---- Survol : tooltip sur la cible la plus proche ----
  const onMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let best = null, bestD = Infinity;
    for (const h of hitsRef.current) {
      const d = Math.hypot(h.x - mx, h.y - my);
      if (d <= h.r + 4 && d < bestD) { best = h; bestD = d; }
    }
    setTooltip(best ? { x: best.x, y: best.y, title: best.title, sub: best.sub } : null);
  };

  const togglePlay = () => {
    if (!playing && t >= duration) setT(0);
    setPlaying((v) => !v);
  };
  const toggleHidden = (pid) => setHidden((h) => ({ ...h, [pid]: !h[pid] }));
  const toggleSelected = (pid) => setSelected((s) => (s === pid ? null : pid));
  const hasFilters = selected !== null || Object.values(hidden).some(Boolean);

  // Ticks d'objectifs/bâtiments sur la barre de temps
  const ticks = useMemo(() => [
    ...(data.events?.objectives || []).map((o) => ({
      t: o.timestamp,
      color: TEAM_COLOR[o.killerTeamId] || '#A78BFA',
      label: `${(MONSTER_META[o.monsterType] || { label: () => 'Monstre' }).label(o)} · ${fmtClock(o.timestamp)}`,
    })),
    ...(data.events?.buildings || []).map((b) => ({
      t: b.timestamp,
      color: 'var(--ink-3)',
      label: `${TOWER_LABELS[b.towerType] || 'Bâtiment'} · ${fmtClock(b.timestamp)}`,
    })),
  ], [data]);

  return (
    <div className="replay-layout">
      <div>
        <div className="replay-map-wrap" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            className="replay-canvas"
            style={{ height: size || undefined }}
            onMouseMove={onMove}
            onMouseLeave={() => setTooltip(null)}
          />
          {tooltip && (
            <div className="replay-tooltip" style={{
              left: Math.min(tooltip.x + 14, Math.max(0, size - 180)),
              top: tooltip.y + 14,
            }}>
              <div className="tt-title">{tooltip.title}</div>
              {tooltip.sub && <div className="tt-sub">{tooltip.sub}</div>}
            </div>
          )}
        </div>

        <div className="replay-controls">
          <button className="btn primary sm" onClick={togglePlay}
                  aria-label={playing ? 'Pause' : 'Lecture'}>
            {playing ? <PauseIcon/> : <PlayIcon/>} {playing ? 'Pause' : 'Lecture'}
          </button>
          <span className="replay-time">{fmtClock(t)} / {fmtClock(duration)}</span>
          <div className="replay-scrub">
            <input
              type="range" min={0} max={duration} step={1000} value={Math.round(t)}
              onChange={(e) => setT(Number(e.target.value))}
              aria-label="Curseur temporel du replay"
            />
            <div className="replay-ticks">
              {ticks.map((tk, i) => (
                <button key={i} className="replay-tick" title={tk.label}
                        style={{ left: `${(tk.t / duration) * 100}%`, background: tk.color }}
                        onClick={() => { setT(tk.t); setPlaying(false); }}/>
              ))}
            </div>
          </div>
          <div className="seg">
            {SPEEDS.map((s) => (
              <button key={s} className={speed === s ? 'active' : ''} onClick={() => setSpeed(s)}>×{s}</button>
            ))}
          </div>
        </div>
        <p className="muted" style={{ fontSize: 11, marginTop: 8, marginBottom: 0 }}>
          Positions issues de la timeline Riot (1 point par minute, interpolées entre deux minutes).
        </p>
      </div>

      <aside>
        <div className="row-bw" style={{ marginBottom: 8 }}>
          <span className="muted" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Joueurs
          </span>
          {hasFilters && (
            <button className="btn ghost sm" onClick={() => { setSelected(null); setHidden({}); }}>
              Réinitialiser
            </button>
          )}
        </div>
        <div className="replay-legend">
          {[100, 200].map((teamId) => (
            <div key={teamId} className="stack-sm" style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: TEAM_COLOR[teamId], letterSpacing: '0.04em' }}>
                {teamId === 100 ? 'ÉQUIPE BLEUE' : 'ÉQUIPE ROUGE'}
              </div>
              {players.filter((p) => p.teamId === teamId).map((p) => {
                const isHidden = !!hidden[p.participantId];
                const isSel = selected === p.participantId;
                return (
                  <div key={p.participantId}
                       className={`replay-player${isSel ? ' selected' : ''}${isHidden ? ' hidden-p' : ''}`}
                       role="button" tabIndex={0}
                       onClick={() => toggleSelected(p.participantId)}
                       onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelected(p.participantId); } }}
                       title={isSel ? 'Cliquer pour désélectionner' : 'Cliquer pour suivre ce joueur'}>
                    <span className="swatch" style={{ background: p.color }}/>
                    <img className="champ-mini" src={champSquare(p.champId)} alt={p.champLabel} loading="lazy"
                         onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}/>
                    <span className="p-name">
                      {p.champLabel}
                      <span className="muted" style={{ fontWeight: 500, marginLeft: 5, fontSize: 11 }}>{p.summonerName}</span>
                    </span>
                    <button className="p-eye"
                            aria-label={isHidden ? `Afficher ${p.champLabel}` : `Masquer ${p.champLabel}`}
                            title={isHidden ? 'Afficher la trajectoire' : 'Masquer la trajectoire'}
                            onClick={(e) => { e.stopPropagation(); toggleHidden(p.participantId); }}>
                      <EyeIcon off={isHidden}/>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="replay-key muted">
          <span><span className="key-dot" style={{ background: TEAM_COLOR[100] }}/> kill (couleur du tueur)</span>
          <span><span className="key-diamond"/> objectif (D drake · B baron · H héraut · L larves · A Atakhan)</span>
          <span><span className="key-x">✕</span> tour / inhibiteur détruit</span>
          <span><span className="key-ring"/> rappel</span>
        </div>
      </aside>
    </div>
  );
}
